import { Modal, Button, Alert } from "flowbite-react";
import { HiExclamation, HiCheckCircle, HiXCircle } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { TicketAiPreCheckResult } from "@/api/ticketApi/ticketAi";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  preCheckResult: TicketAiPreCheckResult | null;
  isLoading: boolean;
  selectedCount: number;
}

function truncate(text: string, max = 60) {
  return text.length > max ? text.substring(0, max) + "..." : text;
}

export default function TicketAiApproveDialog({
  open,
  onClose,
  onConfirm,
  preCheckResult,
  isLoading,
  selectedCount,
}: Props) {
  const { t } = useTranslation(["knowledgeBase", "common"]);

  if (!preCheckResult) return null;

  const { ticketsWithMissingTexts, approvableTickets, ticketsWithoutKb } = preCheckResult;
  const approvableCount = approvableTickets.length;

  return (
    <Modal show={open} onClose={onClose} size="xl">
      <Modal.Header>{t("knowledgeBase:bulkApprove")}</Modal.Header>
      <Modal.Body>
        <div className="space-y-5">
          <p className="text-gray-700 dark:text-gray-300">
            {t("knowledgeBase:ticketApproveSelected", { count: selectedCount })}
          </p>

          {/* Tickets with missing texts */}
          {ticketsWithMissingTexts.length > 0 && (
            <Alert color="failure" icon={HiXCircle}>
              <span className="font-medium">{t("knowledgeBase:missingTextsWarning")}</span>
              <p className="mt-1 text-sm">{t("knowledgeBase:missingTextsDetail", { count: ticketsWithMissingTexts.length })}</p>
              <ul className="mt-2 space-y-1">
                {ticketsWithMissingTexts.map((ticket) => (
                  <li key={ticket.ticket_id} className="flex items-center gap-2 rounded bg-red-50 dark:bg-red-900/20 px-3 py-1.5 text-sm">
                    <HiXCircle className="h-4 w-4 shrink-0 text-red-500" />
                    <span className="truncate">{truncate(ticket.title)}</span>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Approvable tickets */}
          {approvableCount > 0 && (
            <Alert color="success" icon={HiCheckCircle}>
              <span className="font-medium">{t("knowledgeBase:ticketApprovable", { count: approvableCount })}</span>
              <ul className="mt-2 space-y-1">
                {approvableTickets.map((ticket) => (
                  <li key={ticket.ticket_id} className="flex items-center gap-2 rounded bg-green-50 dark:bg-green-900/20 px-3 py-1.5 text-sm">
                    <HiCheckCircle className="h-4 w-4 shrink-0 text-green-500" />
                    <span className="truncate">{truncate(ticket.title)}</span>
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Tickets without KB links - warning only */}
          {ticketsWithoutKb.length > 0 && approvableCount > 0 && (
            <Alert color="warning" icon={HiExclamation}>
              <span className="font-medium">{t("knowledgeBase:noKbLinksWarning")}</span>
              <p className="mt-1 text-sm">{t("knowledgeBase:noKbLinksDetail", { count: ticketsWithoutKb.length })}</p>
            </Alert>
          )}

          {approvableCount === 0 && (
            <Alert color="failure" icon={HiXCircle}>
              <span>{t("knowledgeBase:noTicketsApprovable")}</span>
            </Alert>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {approvableCount > 0 && (
          <Button color="success" onClick={onConfirm} isProcessing={isLoading}>
            <HiCheckCircle className="mr-2 h-5 w-5" />
            {t("knowledgeBase:confirmApprove", { count: approvableCount })}
          </Button>
        )}
        <Button color="gray" onClick={onClose}>
          {t("common:cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
